import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Flame } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";

const Heat = ({ level }) => {
  if (!level) return null;
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Flame key={i} className={`w-3.5 h-3.5 ${i < level ? "text-[#F97316] fill-[#F97316]" : "text-foreground/15"}`} />
      ))}
    </div>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const { t } = useLang();
  const { add } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((r) => { setProduct(r.data); setNotFound(false); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="pt-40 container-pad min-h-screen">Loading…</div>;
  }
  if (notFound || !product) {
    return (
      <div className="pt-40 container-pad min-h-screen">
        <p className="display-serif text-3xl">Product not found.</p>
        <Link to="/products" className="mt-6 inline-block btn-outline">Back to Products</Link>
      </div>
    );
  }

  const hasIngredients = product.ingredients && (product.ingredients.en || product.ingredients.fi || product.ingredients.sv);
  const hasNutrition = Array.isArray(product.nutrition) && product.nutrition.length > 0;
  const hasStorage = product.storage && (product.storage.en || product.storage.fi || product.storage.sv);

  return (
    <div data-testid="product-detail-page" className="pt-32 pb-24">
      <section className="container-pad">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.22em] uppercase text-foreground/60 hover:text-[#F97316] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mt-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* IMAGE */}
          <div className="lg:col-span-6">
            <div className="aspect-[4/5] overflow-hidden bg-[#F5EFE6] sticky top-32" data-testid="product-image">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            </div>
          </div>

          {/* CONTENT */}
          <div className="lg:col-span-6 space-y-14">
            {/* 1. PRODUCT NAME */}
            <header>
              {product.flagship && (
                <div className="eyebrow text-[#F97316] mb-4" data-testid="flagship-label">★ {t.productsSection.flagship}</div>
              )}
              <h1 className="display-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight" data-testid="product-name">
                {product.name}
              </h1>
              <div className="mt-6 flex items-center gap-6">
                <Heat level={product.heat} />
              </div>
            </header>

            {/* 2. SHORT DESCRIPTION */}
            {(product.short_description || product.description) && (
              <section data-testid="short-description">
                <p className="text-lg sm:text-xl font-light leading-[1.6] text-foreground/80">
                  {product.short_description || product.description}
                </p>
              </section>
            )}

            {/* ADD TO CART */}
            <div>
              <button
                data-testid="detail-add-to-cart"
                onClick={() => add(product)}
                className="btn-primary w-full sm:w-auto"
              >
                {t.common.addToCart}
              </button>
            </div>

            {/* 3. NET CONTENT */}
            <section className="border-t border-border pt-10" data-testid="net-content">
              <div className="eyebrow text-foreground/55 mb-3">Net Content</div>
              <div className="display-serif text-2xl sm:text-3xl">{product.net_content || product.size}</div>
            </section>

            {/* 4. INGREDIENTS */}
            {hasIngredients && (
              <section className="border-t border-border pt-10" data-testid="ingredients">
                <div className="eyebrow text-foreground/55 mb-5">Ingredients</div>
                {product.ingredients.en && (
                  <p className="text-base sm:text-lg leading-[1.6] text-foreground" data-testid="ingredients-en">
                    {product.ingredients.en}
                  </p>
                )}
                {(product.ingredients.fi || product.ingredients.sv) && (
                  <div className="mt-5 space-y-2 text-sm text-foreground/55 leading-[1.6]">
                    {product.ingredients.fi && <p data-testid="ingredients-fi"><span className="text-foreground/40 mr-2">FI</span>{product.ingredients.fi}</p>}
                    {product.ingredients.sv && <p data-testid="ingredients-sv"><span className="text-foreground/40 mr-2">SV</span>{product.ingredients.sv}</p>}
                  </div>
                )}
              </section>
            )}

            {/* 5. NUTRITIONAL VALUES */}
            {hasNutrition && (
              <section className="border-t border-border pt-10" data-testid="nutrition">
                <div className="eyebrow text-foreground/55 mb-2">Nutritional Values</div>
                <div className="text-xs text-foreground/45 mb-6">per 100 g</div>
                <ul className="divide-y divide-border">
                  {product.nutrition.map((n) => (
                    <li key={n.key} className="py-4" data-testid={`nutrition-${n.key}`}>
                      <div className="text-base sm:text-lg text-foreground">{n.en}</div>
                      <div className="mt-1 text-xs text-foreground/50 space-y-0.5">
                        <div><span className="text-foreground/35 mr-2">FI</span>{n.fi}</div>
                        <div><span className="text-foreground/35 mr-2">SV</span>{n.sv}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* 6. STORAGE */}
            {hasStorage && (
              <section className="border-t border-border pt-10" data-testid="storage">
                <div className="eyebrow text-foreground/55 mb-5">Storage</div>
                {product.storage.en && (
                  <p className="text-base sm:text-lg leading-[1.6] text-foreground" data-testid="storage-en">
                    {product.storage.en}
                  </p>
                )}
                {(product.storage.fi || product.storage.sv) && (
                  <div className="mt-5 space-y-2 text-sm text-foreground/55 leading-[1.6]">
                    {product.storage.fi && <p data-testid="storage-fi"><span className="text-foreground/40 mr-2">FI</span>{product.storage.fi}</p>}
                    {product.storage.sv && <p data-testid="storage-sv"><span className="text-foreground/40 mr-2">SV</span>{product.storage.sv}</p>}
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
